/**
 * `<PhoneLink phone="…" />` — wraps a phone number in a `tel:` anchor
 * so a tap on iPad / iPhone opens the dialer and a click on a Mac
 * opens FaceTime / continuity-handoff phone call. Same idea as the
 * "Call" button on a Google Maps restaurant card.
 *
 * Behaviors:
 * - Parses through `src/lib/phone.ts` — CA-first, US fallback.
 * - Validation fallback: when libphonenumber rejects the input but
 *   we still have ≥10 digits, we render a `tel:` link with the bare
 *   digits anyway. libphonenumber's CA metadata lags new NPA
 *   assignments (e.g. 467 in Toronto), and a real customer phone
 *   should never silently degrade to un-tappable plain text just
 *   because the bundled metadata is out of date. tel: with garbage
 *   digits is harmless — the OS dialer pre-fills and the user
 *   confirms before dialing — so the safety argument cuts the
 *   other way: a non-tappable phone is the worse failure.
 * - `plain` prop forces text-only rendering. Use it inside a clickable
 *   parent (a `<Link>`, a `<button>` combobox option, etc.) where a
 *   nested anchor would be invalid HTML or steal the parent's click.
 * - When the link IS rendered and the parent has its own click handler
 *   (e.g. the appointment day-list tile is a `role="button"` div),
 *   the link calls `e.stopPropagation()` so calling the customer
 *   doesn't also open the appointment dialog.
 *
 * Renders the em-dash `—` for null/empty input, matching
 * `formatPhone`'s convention so existing layouts don't shift.
 */

import type { CountryCode } from 'libphonenumber-js'
import { parsePhone } from '@/lib/phone'
import { cn } from '@/lib/utils'

type PhoneLinkProps = {
  phone: string | null | undefined
  /** Country hint for parsing; defaults to CA (Autoflo's launch market). */
  country?: CountryCode
  /**
   * Force text-only rendering — use inside a clickable parent (`<Link>`,
   * combobox `<button>`) where a nested anchor would conflict.
   */
  plain?: boolean
  className?: string
}

export function PhoneLink({
  phone,
  country = 'CA',
  plain = false,
  className,
}: PhoneLinkProps) {
  const { e164, display, isValid } = parsePhone(phone, country)

  if (!phone || display === '—' || plain) {
    return <span className={className}>{display}</span>
  }

  // Prefer the validated E.164; otherwise fall back to digits-only so
  // numbers whose area code isn't in the bundled libphonenumber
  // metadata (e.g. CA 467) still produce a tappable dialer link.
  let href: string | null = isValid ? `tel:${e164}` : null
  if (!href) {
    const digits = String(phone).replace(/\D/g, '')
    if (digits.length >= 10) {
      const plusPrefix = String(phone).trim().startsWith('+') ? '+' : ''
      href = `tel:${plusPrefix}${digits}`
    }
  }

  if (!href) {
    return <span className={className}>{display}</span>
  }

  return (
    <a
      href={href}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'text-inherit underline-offset-2 hover:text-brand hover:underline',
        className,
      )}
    >
      {display}
    </a>
  )
}
